import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useAuthSession } from '../../lib/authSession'
import BookActionController from './BookActionController'
import CapitalMatchingPane from './CapitalMatchingPane'
import DocumentLink from '../documents/DocumentLink'
import HeldRequestDraft from './HeldRequestDraft'
import HeldDismissalAction from './HeldDismissalAction'
import HeldSourceInterpretationAction from './HeldSourceInterpretationAction'
import { mapBookActions } from './bookReviewApi'
import { reviewBindingScope } from './bookActionsPanel'
import { SOURCE_ANSWER_REOPENED_MESSAGE } from './bookWorkApi'
import type { BookWorkResponse } from './bookWorkApi'
import { createBookPendingGroup } from './bookWorkLifecycle'

type PendingGroup = ReturnType<typeof createBookPendingGroup>
interface Props {
  groupId: string
  work: BookWorkResponse
  onRefresh?: () => void
  onLocalRefresh?: () => void
  onApplied?: () => void
  onPendingChange?: (pending: boolean) => void
}

function PendingChild({
  group,
  children,
}: {
  group: PendingGroup
  children: (report: (pending: boolean) => void) => ReactNode
}) {
  const owner = useRef<ReturnType<PendingGroup['attach']> | null>(null)
  useEffect(() => {
    const attached = group.attach()
    owner.current = attached
    return () => {
      owner.current = null
      attached.release()
    }
  }, [group])
  const report = useCallback((pending: boolean) => owner.current?.set(pending), [])
  return <>{children(report)}</>
}

export default function BookWorkContent(props: Props) {
  const session = useAuthSession()
  if (props.work.group_id !== props.groupId)
    return <p role="status">This Book review belongs to a different selection.</p>
  return (
    <ScopedBookWork
      key={`${session}:${props.groupId}:${props.work.scope.fund_id ?? ''}:${props.work.scope.client_id ?? ''}`}
      {...props}
    />
  )
}

function ScopedBookWork({
  groupId,
  work,
  onRefresh,
  onLocalRefresh,
  onApplied,
  onPendingChange,
}: Props) {
  const callbacks = useRef({ onRefresh, onLocalRefresh, onPendingChange })
  callbacks.current = { onRefresh, onLocalRefresh, onPendingChange }
  const [pending] = useState(() =>
    createBookPendingGroup(
      (value) => callbacks.current.onPendingChange?.(value),
      () => callbacks.current.onRefresh?.(),
      () => (callbacks.current.onLocalRefresh ?? callbacks.current.onRefresh)?.()
    )
  )
  useEffect(() => {
    pending.activate()
    return () => pending.dispose()
  }, [pending])
  const scope = reviewBindingScope(work.scope)
  return (
    <>
      {work.ordinary ? (
        <PendingChild key={`ordinary:${work.ordinary.review.binding.value}`} group={pending}>
          {(report) => (
            <BookActionController
              groupId={groupId}
              review={work.ordinary!.review}
              actions={mapBookActions(work.ordinary!)}
              deckVersion={work.ordinary!.deck_version}
              scope={scope}
              onRefresh={pending.requestRefresh}
              onApplied={onApplied}
              onPendingChange={report}
            />
          )}
        </PendingChild>
      ) : null}
      {work.matching.map((review) => (
        <PendingChild
          key={`matching:${review.proposal_id}:${review.reviewed_binding}`}
          group={pending}
        >
          {(report) => (
            <CapitalMatchingPane
              review={review}
              scope={scope}
              onRefresh={pending.requestRefresh}
              onApplied={onApplied}
              onPendingChange={report}
            />
          )}
        </PendingChild>
      ))}
      {(work.held_items ?? []).map((item) => (
        <section key={`${item.item_id}:${item.revision}`} aria-label="Document review">
          <h3>{item.kind === 'terms' ? 'Terms review' : 'Transaction review'}</h3>
          <p role="status">
            {item.state === 'needs_review'
              ? 'Needs review'
              : item.state === 'stale'
                ? 'The source or context has changed.'
                : item.state === 'settled' || item.state === 'not_current'
                  ? 'This review is no longer pending.'
                  : 'Review not ready.'}
          </p>
          {/* Shown with the complete question too: without it the officer sees
              the same question again and no reason why. */}
          {item.state === 'needs_review' &&
          item.messages.includes(SOURCE_ANSWER_REOPENED_MESSAGE) &&
          work.held_questions?.find(
            (review) => review.item_id === item.item_id && review.revision === item.revision
          )?.complete ? (
            <p role="status">{SOURCE_ANSWER_REOPENED_MESSAGE}</p>
          ) : null}
          {work.held_questions?.find(
            (review) => review.item_id === item.item_id && review.revision === item.revision
          )?.complete ? (
            work.held_questions
              .find((review) => review.item_id === item.item_id)!
              .questions.map((question) => (
                <div key={question.field} aria-label="Officer question">
                  <p>{question.prompt}</p>
                  <p>{question.reason}</p>
                  {question.documents.map((document, index) => (
                    <div key={`${document.document_id}:${document.page}:${index}`}>
                      <DocumentLink document={document} context={{ fundId: work.scope.fund_id }} />
                      <span> · Page {document.page}</span>
                      <blockquote>{document.snippet}</blockquote>
                    </div>
                  ))}
                  {work.scope.fund_id && item.state === 'needs_review'
                    ? (work.held_source_interpretation_options ?? [])
                        .filter(
                          (option) =>
                            option.item_id === item.item_id && option.field === question.field
                        )
                        .map((option) => (
                          <PendingChild
                            key={`interpretation:${item.item_id}:${item.revision}:${option.field}`}
                            group={pending}
                          >
                            {(report) => (
                              <HeldSourceInterpretationAction
                                item={item}
                                option={option}
                                fundId={work.scope.fund_id!}
                                onSaved={pending.requestRefresh}
                                onPendingChange={report}
                              />
                            )}
                          </PendingChild>
                        ))
                    : null}
                </div>
              ))
          ) : (
            <>
              {item.messages.map((message, index) => (
                <p key={index}>{message}</p>
              ))}
              {item.state === 'needs_review' && work.held_questions ? (
                <p role="status">The review does not yet provide a verified question and source.</p>
              ) : null}
            </>
          )}
          {(work.saved_held_dismissals?.dismissals ?? [])
            .filter((row) => row.item_id === item.item_id)
            .map((row) => (
              <div key={row.dismissal_id}>
                <p>Marked not relevant{row.earlier_review ? ' · Earlier review' : ''}</p>
                <p>
                  Recorded by{' '}
                  {work.held_dismissal_authors?.find((author) => author.actor_id === row.actor_id)
                    ?.display_name ?? `officer ${row.actor_id}`}
                </p>
                <p>{row.reason}</p>
              </div>
            ))}
          {work.saved_held_dismissals?.complete === false ? (
            <p role="status">
              Saved dismissal notes could not be fully verified. Refresh to see their current
              status.
            </p>
          ) : null}
          {work.scope.fund_id && work.held_dismissal_available_ids?.includes(item.item_id) ? (
            <PendingChild key={`dismissal:${item.item_id}:${item.revision}`} group={pending}>
              {(report) => (
                <HeldDismissalAction
                  item={item}
                  fundId={work.scope.fund_id!}
                  onSaved={pending.requestRefresh}
                  onPendingChange={report}
                />
              )}
            </PendingChild>
          ) : null}
          {(work.saved_held_requests?.requests ?? [])
            .filter((row) => row.item_id === item.item_id)
            .map((row) => (
              <div key={row.request_id}>
                <p>{row.requested_document}</p>
                <p>{row.counterparty_name}</p>
                <p>Draft — not sent{row.earlier_review ? ' · Earlier review' : ''}</p>
              </div>
            ))}
          {work.saved_held_requests?.complete === false ? (
            <p role="status">
              Saved requests could not be fully verified. Refresh before preparing another draft.
            </p>
          ) : null}
          {item.state === 'needs_review' &&
          item.request_draft_available &&
          (work.scope.fund_id || work.scope.client_id) &&
          work.saved_held_requests?.complete !== false ? (
            <PendingChild group={pending}>
              {(report) => (
                <HeldRequestDraft
                  key={(work.saved_held_requests?.requests ?? [])
                    .filter((row) => row.item_id === item.item_id)
                    .map((row) => row.request_id)
                    .join(':')}
                  item={item}
                  hasSaved={work.saved_held_requests?.requests.some(
                    (row) => row.item_id === item.item_id
                  )}
                  onSaved={scope.clientId ? pending.requestLocalRefresh : pending.requestRefresh}
                  scope={work.scope}
                  onPendingChange={report}
                />
              )}
            </PendingChild>
          ) : null}
        </section>
      ))}
      {work.unavailable ? (
        <p role="status">
          Other selected Book items remain pending. Their current review is unavailable.
        </p>
      ) : null}
    </>
  )
}
